import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Play, CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [discovery, setDiscovery] = useState(null);
  const [agentTasks, setAgentTasks] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    fetchTicketData();
    const interval = setInterval(fetchTicketData, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, [id]);

  const fetchTicketData = async () => {
    try {
      const [ticketRes, agentRes, deploymentRes, auditRes] = await Promise.all([
        axios.get(`${API}/tickets/${id}`),
        axios.get(`${API}/agents/${id}/tasks`),
        axios.get(`${API}/deployments/${id}`),
        axios.get(`${API}/audit/${id}`)
      ]);

      setTicket(ticketRes.data);
      setAgentTasks(agentRes.data);
      setDeployments(deploymentRes.data);
      setAuditLogs(auditRes.data);

      // Try to fetch discovery if available
      try {
        const discoveryRes = await axios.get(`${API}/discovery/${id}`);
        setDiscovery(discoveryRes.data);
      } catch (err) {
        // Discovery not yet run
      }
    } catch (error) {
      console.error('Error fetching ticket data:', error);
    } finally {
      setLoading(false);
    }
  };

  const runDiscovery = async () => {
    setExecuting(true);
    try {
      await axios.post(`${API}/discovery/${id}`);
      setTimeout(fetchTicketData, 2000);
    } catch (error) {
      console.error('Error running discovery:', error);
    } finally {
      setExecuting(false);
    }
  };

  const executeAgent = async (agentType) => {
    setExecuting(true);
    try {
      await axios.post(`${API}/agents/${id}/execute/${agentType}`);
      setTimeout(fetchTicketData, 2000);
    } catch (error) {
      console.error(`Error executing ${agentType} agent:`, error);
    } finally {
      setExecuting(false);
    }
  };

  const executeFullWorkflow = async () => {
    setExecuting(true);
    try {
      await axios.post(`${API}/agents/${id}/workflow`);
      // Refresh data every 3 seconds for 30 seconds
      let count = 0;
      const interval = setInterval(() => {
        fetchTicketData();
        count++;
        if (count >= 10) clearInterval(interval);
      }, 3000);
    } catch (error) {
      console.error('Error executing workflow:', error);
    } finally {
      setExecuting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-gray-500',
      'in_analysis': 'bg-blue-500',
      'in_design': 'bg-purple-500',
      'in_development': 'bg-indigo-500',
      'in_testing': 'bg-amber-500',
      'in_deployment': 'bg-orange-500',
      'in_governance': 'bg-cyan-500',
      'completed': 'bg-green-500',
      'rejected': 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getAgentIcon = (status) => {
    if (status === 'completed') return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (status === 'failed') return <XCircle className="h-5 w-5 text-red-600" />;
    if (status === 'in_progress') return <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />;
    return <Clock className="h-5 w-5 text-gray-400" />;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  if (!ticket) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Ticket not found</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="ticket-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/tickets')} data-testid="back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="font-mono">{ticket.ticket_number}</Badge>
              <Badge className={getStatusColor(ticket.status)}>{ticket.status.replace('_', ' ')}</Badge>
            </div>
            <h1 className="text-3xl font-bold">{ticket.title}</h1>
          </div>
        </div>
        <div className="flex gap-2">
          {!discovery && (
            <Button onClick={runDiscovery} disabled={executing} data-testid="run-discovery-btn">
              {executing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Run Discovery
            </Button>
          )}
          {discovery && (
            <Button onClick={executeFullWorkflow} disabled={executing} data-testid="execute-workflow-btn">
              {executing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Execute Full Workflow
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Ticket Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Primary Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-sm text-muted-foreground mb-1">Ticket Number</div>
                <div className="font-medium font-mono">{ticket.ticket_number}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Company</div>
                <div className="font-medium">{ticket.company || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Category</div>
                <div className="font-medium">{ticket.category || ticket.customization_type || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Priority</div>
                <Badge variant="outline">{ticket.priority}</Badge>
              </div>
            </div>

            {/* Short Description */}
            <div>
              <div className="text-sm text-muted-foreground mb-1">Short Description</div>
              <div className="font-medium">{ticket.short_description || ticket.title}</div>
            </div>

            {/* Full Description */}
            <div>
              <div className="text-sm text-muted-foreground mb-1">Description</div>
              <div className="font-medium whitespace-pre-wrap bg-slate-50 p-3 rounded-lg border">
                {ticket.description || 'No description provided'}
              </div>
            </div>

            {/* Work Notes */}
            {ticket.work_notes && (
              <div>
                <div className="text-sm text-muted-foreground mb-1">Work Notes</div>
                <div className="font-medium whitespace-pre-wrap bg-amber-50 p-3 rounded-lg border border-amber-200 max-h-64 overflow-auto text-sm">
                  {ticket.work_notes}
                </div>
              </div>
            )}

            {/* Secondary Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t">
              <div>
                <div className="text-sm text-muted-foreground mb-1">Assignment Group</div>
                <div className="font-medium">{ticket.assignment_group || 'Unassigned'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Created By</div>
                <div className="font-medium">{ticket.created_by || ticket.requester_name || 'Unknown'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Created Date</div>
                <div className="font-medium">{new Date(ticket.created_at).toLocaleString()}</div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
              <div>
                <div className="text-sm text-muted-foreground mb-1">SAP System</div>
                <div className="font-medium">{ticket.sap_system}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Source</div>
                <div className="font-medium">{ticket.source}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Requester</div>
                <div className="font-medium">{ticket.requester_name || 'Unknown'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Email</div>
                <div className="font-medium text-sm">{ticket.requester_email || 'N/A'}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="agents" className="w-full">
        <TabsList>
          <TabsTrigger value="agents">Agent Tasks</TabsTrigger>
          <TabsTrigger value="discovery">Discovery</TabsTrigger>
          <TabsTrigger value="deployments">Deployments</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="agents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Agent Orchestration</CardTitle>
              <CardDescription>Multi-agent workflow execution status</CardDescription>
            </CardHeader>
            <CardContent>
              {agentTasks.length === 0 ? (
                <Alert>
                  <AlertDescription>No agent tasks executed yet. Run discovery first, then execute the full workflow.</AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-4">
                  {agentTasks.map((task) => (
                    <div key={task.id} className="flex items-start gap-4 p-4 border rounded-lg" data-testid={`agent-task-${task.agent_type}`}>
                      <div className="mt-1">{getAgentIcon(task.status)}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="font-semibold text-lg capitalize">{task.agent_type} Agent</div>
                          <Badge variant={task.status === 'completed' ? 'default' : 'secondary'}>{task.status}</Badge>
                        </div>
                        {task.started_at && (
                          <div className="text-sm text-muted-foreground">
                            Started: {new Date(task.started_at).toLocaleString()}
                          </div>
                        )}
                        {task.completed_at && (
                          <div className="text-sm text-muted-foreground">
                            Completed: {new Date(task.completed_at).toLocaleString()}
                          </div>
                        )}
                        {task.error_message && (
                          <Alert variant="destructive" className="mt-2">
                            <AlertDescription>{task.error_message}</AlertDescription>
                          </Alert>
                        )}
                      </div>
                      {task.status === 'pending' && (
                        <Button size="sm" onClick={() => executeAgent(task.agent_type)} disabled={executing}>
                          Execute
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="discovery">
          {discovery ? (
            <Card>
              <CardHeader>
                <CardTitle>Discovery Results</CardTitle>
                <CardDescription>SAP landscape analysis and Clean Core compliance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 border rounded-lg">
                      <div className="text-2xl font-bold">{discovery.total_customizations}</div>
                      <div className="text-sm text-muted-foreground">Total Customizations</div>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{discovery.clean_core_compliant}</div>
                      <div className="text-sm text-muted-foreground">Clean Core Compliant</div>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{discovery.non_compliant}</div>
                      <div className="text-sm text-muted-foreground">Non-Compliant</div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Findings</h3>
                    <div className="space-y-2">
                      {discovery.findings.map((finding, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <div className="font-medium">{finding.name}</div>
                            <Badge variant={finding.clean_core_status === 'compliant' ? 'default' : 'destructive'}>
                              {finding.clean_core_status.replace('_', ' ')}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground mb-1">{finding.type}: {finding.description}</div>
                          <div className="text-sm text-blue-600">Recommendation: {finding.recommendation}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Recommendations</h3>
                    <ul className="list-disc list-inside space-y-1">
                      {discovery.recommendations.map((rec, idx) => (
                        <li key={idx} className="text-sm">{rec}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground mb-4">Discovery not yet run for this ticket</p>
                <Button onClick={runDiscovery} disabled={executing}>
                  {executing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                  Run Discovery
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="deployments">
          <Card>
            <CardHeader>
              <CardTitle>Deployment Pipeline</CardTitle>
              <CardDescription>DEV → QA → PROD deployment tracking</CardDescription>
            </CardHeader>
            <CardContent>
              {deployments.length === 0 ? (
                <Alert>
                  <AlertDescription>No deployments yet. Complete the agent workflow first.</AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-3">
                  {deployments.map((deployment) => (
                    <div key={deployment.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge>{deployment.environment}</Badge>
                          <Badge variant={deployment.status === 'success' ? 'default' : 'secondary'}>
                            {deployment.status}
                          </Badge>
                        </div>
                        {deployment.transport_number && (
                          <div className="text-sm font-mono text-muted-foreground">{deployment.transport_number}</div>
                        )}
                      </div>
                      {deployment.deployment_logs && deployment.deployment_logs.length > 0 && (
                        <div className="text-sm text-muted-foreground">
                          {deployment.deployment_logs[deployment.deployment_logs.length - 1]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle>Audit Log</CardTitle>
              <CardDescription>Complete audit trail of all actions</CardDescription>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <Alert>
                  <AlertDescription>No audit logs yet</AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-2">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="flex items-start gap-4 p-3 border rounded-lg text-sm">
                      <div className="flex-1">
                        <div className="font-medium">{log.action.replace('_', ' ')}</div>
                        <div className="text-muted-foreground text-xs mt-1">
                          {new Date(log.timestamp).toLocaleString()} • {log.actor}
                        </div>
                      </div>
                      {log.compliance_status && (
                        <Badge variant="outline">{log.compliance_status}</Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
